<template>
  <div>
    <div
      class="m-0.5 flex flex-col gap-6 rounded-lg border bg-white p-4 outline outline-2 transition-all"
      :class="{
        'border border-black outline-black': selected,
        'cursor-pointer border outline-transparent': !selected,
        'border-shade-300': !selected || disabled,
        'text-shade-400': disabled,
      }"
      @click="(e: any) => $emit('click:select', e)"
    >
      <div class="flex w-full justify-between gap-x-2 p-2">
        <slot name="badge"></slot>
        <div
          class="size-5 shrink-0 rounded-full ring-inset transition-shadow"
          :class="{
            'ring-[6px] ring-black': selected,
            'ring-2': !selected,
            'ring-shade-300': disabled || !selected,
          }"
        ></div>
      </div>

      <ProductImage
        v-if="image"
        :image="image"
        image-classes="aspect-[5/3]"
        :alt="`${name} ${title}`"
        sizes="320px"
        image-loading="eager"
        :should-trim="true"
        :class="{
          'opacity-40': !isAvailable,
        }"
      />

      <div class="flex flex-col gap-6 px-4">
        <hr
          class="w-full border border-x-0 border-b-0 border-shade-500 opacity-25"
        />
        <div>
          <p v-if="title" class="text-base font-medium uppercase leading-tight">
            {{ title }}
          </p>
          <p class="text-base leading-tight">
            {{ name }}
          </p>

          <BazaarVoice
            :id="product.id"
            show="inline_rating"
            :auto-hide="false"
          />
          <slot name="price" :price="price">
            <ProductPriceLegacy
              :price="price"
              :applied-reductions="price.appliedReductions"
              :show-automatic-discount="!isBuyXGetYPrioritized"
              :zero-cost-product="isZeroCostProduct"
              size="lg"
              :show-price-from="hasVariantsWithDifferentPrice"
            />
            <div v-if="isZeroCostProduct" class="max-w-3xs text-xs">
              {{ $t('zero_cost_frames.price_hint') }}
            </div>
            <span v-if="lowestPrice" class="text-xs text-gray-600">
              {{ $t('pdp.lowest_price') }}
              {{ `${lowestPrice}` }}
            </span>
            <span v-if="rxConfiguratorEnabled && lensesName" class="text-xs">
              {{
                $t('pdp.with_cheapest_compatible_lenses', {
                  lensesName,
                })
              }}
            </span>
            <span
              v-if="
                !hasVariantsWithDifferentPrice &&
                isCareProduct &&
                careProductBasePriceText
              "
              class="text-xs text-gray-600"
            >
              {{ careProductBasePriceText }}
            </span>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Price, Product } from '@scayle/storefront-core'

type Props = {
  product: Product
  selected?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  disabled: false,
})

const { $currentShop, $featureToggle, $i18n } = useNuxtApp()
const {
  getCheapestLensForFrame,
  getCombinedProductPrice,
  getCombinedLowestPriorPrice,
} = await useProductPrice()

const { isRxConfiguratorEnabled, isZeroCostEnabled } = $featureToggle
const rxConfiguratorEnabled = computed(() =>
  isRxConfiguratorEnabled(props.product),
)

const { isBuyXGetYPrioritized } = await useProductPromotions(props.product)
const { product: lensProduct } = getCheapestLensForFrame(props.product)

const isZeroCostProduct = computed(() => {
  return isZeroCostEnabled() && price.value.withoutTax === 0
})

const image = computed(() => getPreviewImage(props.product))
const title = computed(() =>
  getFirstAttributeLabel(props.product.attributes, 'brand'),
)
const name = computed(() =>
  getFirstAttributeLabel(props.product.attributes, 'name'),
)

const isCareProduct = computed(() => isCareProducts(props.product))
const isAvailable = computed(() => isProductAvailable(props.product))

const priceRange = computed(() => {
  const getMin = (p: Product) => p?.priceRange?.min
  const getMax = (p: Product) => p?.priceRange?.max

  const compare = (a: Price | undefined, b: Price | undefined) => {
    if (a && b && a.withTax > b.withTax) {
      return 1
    } else if (a && b && a.withTax === b.withTax) {
      return 0
    } else {
      return -1
    }
  }
  const initial = { min: getMin(props.product), max: getMax(props.product) }

  return (
    props.product?.siblings?.reduce((result, sibling) => {
      const min = getMin(sibling)
      const max = getMax(sibling)
      return {
        min: compare(result.min, min) > 0 ? min : result.min,
        max: compare(result.max, max) > 0 ? result.max : max,
      }
    }, initial) ?? initial
  )
})

const hasPriceRange = computed(
  () =>
    (priceRange.value.min?.withTax ?? Infinity) <
    (priceRange.value.max?.withTax ?? 0),
)

const siblingCount = computed(() =>
  getProductSiblingCount(props.product, $currentShop),
)

const price = computed(() => getCombinedProductPrice(props.product))

const hasVariantsWithDifferentPrice = computed(() => {
  if (rxConfiguratorEnabled.value) {
    return true
  }

  // Contact lens with multiple package sizes
  if (
    isContactLens(props.product) &&
    siblingCount.value &&
    siblingCount.value > 1
  ) {
    return true
  }

  return hasPriceRange.value
})
const lensesName = computed(() => getLensName(lensProduct))

const lowestPrice = computed(() => {
  const combinedLowestPriorPrice = getCombinedLowestPriorPrice(
    toValue(props.product),
  )

  return getLowestPriorPrice(price.value, combinedLowestPriorPrice)
})

const careProductBasePriceText = computed(() => {
  if (props.product?.variants) {
    const packageSize = getFirstAttributeValue(
      props.product.variants[0].attributes,
      'packingTypeDescription',
    )?.label
    const referencePriceWithTax =
      props.product.variants[0].price?.reference?.withTax
    if (packageSize && referencePriceWithTax) {
      return getBasePriceFormatted(
        packageSize,
        referencePriceWithTax,
        $i18n.t('litre'),
      )
    }
  }
  return ''
})
defineEmits(['click:select'])
</script>
